/**
 * TenantSwitchContext — platform-operator "enter tenant" / "return to platform"
 * state for the platform ↔ tenant control-plane split (Wave 2).
 *
 * When a platform operator (founder, or a holder of `platform.tenants.impersonate`)
 * "enters" a tenant, we record that tenant's uuid. `lib/api.ts` reads it via the
 * module-level getter below and attaches it as `X-Tenant-Id` on every request; the
 * backend's tenant middleware then scopes the request into that tenant and files an
 * access-transparency `scope_override` entry.
 *
 * BYTE-IDENTITY GUARANTEE: when NO tenant is entered (the default for every FNS
 * user and for founders who haven't switched) `getActiveTenantOverride()` returns
 * null, so api.ts sends NO extra header — requests are byte-identical to today.
 *
 * The override is persisted to sessionStorage so a page refresh keeps the operator
 * scoped into the tenant until they explicitly return to the platform.
 */
import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

const STORAGE_KEY = 'fnsai.tenantSwitch';

// Module-level mirror of the active override. This exists so NON-React consumers
// — specifically the axios request interceptor in lib/api.ts, which is not a React
// hook — can read the current impersonation without a hook. The provider keeps this
// in sync with React state + sessionStorage on every enter/return.
let activeTenantOverride: string | null = null;

interface StoredSwitch {
  id: string;
  name: string | null;
}

function readStored(): StoredSwitch | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSwitch;
    if (parsed && typeof parsed.id === 'string' && parsed.id) {
      return { id: parsed.id, name: typeof parsed.name === 'string' ? parsed.name : null };
    }
  } catch {
    /* private mode / corrupt value — treat as no override */
  }
  return null;
}

// Seed the module mirror from sessionStorage at import time so the very first
// axios request after a hard refresh already carries the override, before React
// has mounted the provider.
activeTenantOverride = readStored()?.id ?? null;

/**
 * Plain (non-hook) getter for the active tenant override uuid, or null when the
 * operator is on the platform. `lib/api.ts`'s request interceptor calls this to
 * decide whether to attach the `X-Tenant-Id` header. Returns null → no header.
 */
/**
 * Drop any impersonation override, for callers outside React — sign-out in
 * particular. Without this the override outlives the session in a tab, and
 * the NEXT person to sign in there starts scoped into someone else's tenant.
 * No navigation: the caller is already going somewhere.
 */
export function clearTenantOverride(): void {
  activeTenantOverride = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* best-effort — private mode */
  }
}

export function getActiveTenantOverride(): string | null {
  return activeTenantOverride;
}

interface TenantSwitchValue {
  activeTenantId: string | null;
  activeTenantName: string | null;
  /** Enter (impersonate) a tenant by uuid. Sets the override for all subsequent requests. */
  enterTenant: (id: string, name?: string | null) => void;
  /** Clear the override and reload so all data re-scopes back to the platform. */
  returnToPlatform: () => void;
}

const TenantSwitchContext = createContext<TenantSwitchValue>({
  activeTenantId: null,
  activeTenantName: null,
  enterTenant: () => {},
  returnToPlatform: () => {},
});

export function TenantSwitchProvider({ children }: { children: ReactNode }) {
  const initial = readStored();
  const [activeTenantId, setActiveTenantId] = useState<string | null>(initial?.id ?? null);
  const [activeTenantName, setActiveTenantName] = useState<string | null>(initial?.name ?? null);

  const enterTenant = useCallback((id: string, name?: string | null) => {
    if (!id) return;
    const nm = name ?? null;
    // Keep the three sources of truth in lockstep: module mirror (for api.ts),
    // sessionStorage (for refresh persistence), and React state (for the banner).
    activeTenantOverride = id;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, name: nm } satisfies StoredSwitch));
    } catch {
      /* private mode — persistence is best-effort */
    }
    setActiveTenantId(id);
    setActiveTenantName(nm);
    // Load the tenant's workspace freshly. Without this, already-fetched
    // platform/founding data (fetched WITHOUT X-Tenant-Id) would linger in the
    // React Query cache under the "scoped into this tenant" banner until each
    // query happened to refetch — showing the operator the wrong tenant's data.
    // The override is already in the module mirror + sessionStorage, so the very
    // first request after this navigation carries X-Tenant-Id. Mirrors
    // returnToPlatform's reload. Go to the app root so the operator lands in the
    // entered tenant's workspace rather than the platform console.
    try {
      window.location.assign('/');
    } catch {
      /* noop in non-browser test envs */
    }
  }, []);

  const returnToPlatform = useCallback(() => {
    activeTenantOverride = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* best-effort */
    }
    setActiveTenantId(null);
    setActiveTenantName(null);
    // Full reload: drops any tenant-scoped React Query cache and re-issues every
    // request WITHOUT the X-Tenant-Id header, so all data re-scopes to the platform.
    try {
      window.location.reload();
    } catch {
      /* noop in non-browser test envs */
    }
  }, []);

  const value = useMemo<TenantSwitchValue>(
    () => ({ activeTenantId, activeTenantName, enterTenant, returnToPlatform }),
    [activeTenantId, activeTenantName, enterTenant, returnToPlatform],
  );

  return <TenantSwitchContext.Provider value={value}>{children}</TenantSwitchContext.Provider>;
}

/**
 * Hook exposing the tenant-switch API. W2-A2's Tenants.tsx calls
 * `useTenantSwitch().enterTenant(id, name)` from each row's "Enter this tenant"
 * button; Topbar calls `returnToPlatform()` from the impersonation banner.
 */
export function useTenantSwitch(): TenantSwitchValue {
  return useContext(TenantSwitchContext);
}
